<template>
  <div>
    <div class="sl_deleted" v-if="isDeleted">
      <h2>This task has been deleted.</h2>
      <v-btn :to="'/projects/' + $route.params.project_id + '/tasks'" color="success">Go to project page</v-btn>
    </div>
    <v-container v-else fluid grid-list-lg>
      <v-toolbar class='py-2 mb-3 task_page__toolbar'>
        <v-btn icon :to="{name: 'tasks-list', params: { project_id: $route.params.project_id } }">
          <v-icon>arrow_back</v-icon>
        </v-btn>
        <v-toolbar-title class='caption '>
          <div v-show="editing.title">
            <v-textarea
                hide-details
                class="ma-0 pa-0"
                v-model="task.title"
                ref="title"
                solo
                @click:append="update"
                :append-icon="editing.title && $route.params.task_id > 0 && canSave > 0 ? 'save' : ''"
                auto-grow
                rows="1"
            ></v-textarea>
            <div class="text-xs-right" :style="{ color: !canSave ? 'red' : '' }">
              {{ task.title.length }} / 190
            </div>
          </div>
          <span class="title" @dblclick="edit('title')" v-show="!editing.title">{{ task.title }}</span>
        </v-toolbar-title>
        <v-spacer></v-spacer>

        <div v-if="isDesktop">
          <div v-if="task.deleted_at != null" class="archive-wrapper">
            This task is in archive.
            <v-btn small icon title="Restore" color="success" @click="restoreTask(task)">
              <v-icon size="18">restore</v-icon>
            </v-btn>
            <remove-dialog :yes="() => forceDelete(task)">
              <v-btn small icon title="Delete forever" color="error">
                <v-icon size="18">delete_forever</v-icon>
              </v-btn>
            </remove-dialog>
          </div>
          <div v-else>
            <v-btn v-if="!task.id" :disabled="!canSave" class="my-0" color="success" @click="create">save</v-btn>
            <remove-dialog v-if="task.id" :yes="() => removeTask(task)">
              <v-btn class="my-0" color="error">Archive</v-btn>
            </remove-dialog>
          </div>
        </div>
        <div v-else>
          <v-btn v-if="!task.id" class="my-0" color="success" smll icon @click="create">
            <v-icon size="18">check</v-icon>
          </v-btn>
          <v-menu :close-on-content-click="false" v-else bottom offset-y right>
            <v-btn slot="activator" small icon flat class='ma-0' color="secondary">
              <v-icon>more_vert</v-icon>
            </v-btn>
            <v-card>
              <v-card-text>
                <div v-if="task.deleted_at != null">
                  This task is in archive.<br>
                  <v-btn small block title="Restore" color="success" @click="restoreTask(task)">Restore
                  </v-btn>
                  <remove-dialog :yes="() => forceDelete(task)" style="width: 100%">
                    <v-btn small block title="Delete forever" color="error">Delete permanently</v-btn>
                  </remove-dialog>
                </div>
                <div v-else>
                  <remove-dialog v-if="task.id" :yes="() => removeTask(task)">
                    <v-btn class="my-0" color="error">Archive</v-btn>
                  </remove-dialog>
                </div>
              </v-card-text>
            </v-card>
          </v-menu>
        </div>
      </v-toolbar>
      <progress-bar></progress-bar>
      <v-layout wrap mt-2>
        <v-flex sm12 md8>
          <!-- Add v-select for task templates (only for new tasks) -->
          <v-card class="mb-3" v-if="!task.id">
            <v-card-text>
              <v-select
                :items="taskTemplates"
                item-text="title"
                item-value="key"
                label="Use task template"
                hide-details
                v-model="selectedTemplate"
                @change="onTemplateSelect"
                clearable
              ></v-select>
            </v-card-text>
          </v-card>
          <drop-zone @paste="(file) => {task.attachments.push(file)}" @update="update">
            <v-card>
              <v-card-text>
                <div class='sl_form_title'>
                  <h3>
                    <span class="sl_form_title--project-name">
                      Project: <strong>{{ project | getText('name', 'without project') }}</strong><br>
                    </span>
                    Description task
                    <v-btn
                      v-if="editing.description && $route.params.task_id > 0"
                      small
                      icon
                      class="my-0"
                      @click="update"
                    >
                      <v-icon size="18" color="info">save</v-icon>
                    </v-btn>
                  </h3>
                  <v-spacer></v-spacer>
                  <template v-if="!editing.description && $route.params.task_id > 0">
                    <v-btn
                      v-if="task.id"
                      small
                      color="success"
                      @click="editTask('description', false)"
                    >
                      Edit Task
                    </v-btn>
                    <v-btn
                      v-if="task.id"
                      small
                      color="success"
                      @click="$refs.FormSubTask.toggle()"
                    >
                      Add subtask
                    </v-btn>
                  </template>
                </div>
                <v-divider class='my-2'></v-divider>
                <markdown
                    v-viewer
                    class="task_page__description"
                    label="Task description"
                    ref="description"
                    v-model="task.text"
                    :editing="editing.description"
                    @paste="paste"
                />
              </v-card-text>
            </v-card>
          </drop-zone>

          <v-card v-if="task.children && task.children.length > 0" class="mt-3">
            <v-card-text>
              <task-children :children="task.children" @refresh="refresh"/>
            </v-card-text>
          </v-card>

          <v-card class="mt-3">
            <v-card-text>
              <task-comments
                ref="comments"
                :task_id="$route.params.task_id"
                :isLoading="active"
              />
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex sm12 md4>
          <v-card>
            <v-card-text>
              <div class='sl_form_title align-center'>
                <h3>Management</h3>
                <v-menu :close-on-content-click="false" close-on-click bottom offset-y min-width="280">
                  <v-btn slot="activator" class='ma-0' small :icon="isMobile" icon>
                    <v-icon>settings</v-icon>
                  </v-btn>
                  <v-card dense>
                    <v-card-text class="pa-2">
                      <h3 class="mb-1">Notifications</h3>
                      <v-divider></v-divider>
                      <v-checkbox
                        class="my-2"
                        hide-details
                        color="success"
                        label="Task status change"
                        v-model="taskData.state"
                      ></v-checkbox>
                      <div class="mt-3">
                        <v-btn block small elevation="2" color="success" @click="setManagementNotifications">
                          <v-icon class="mr-1" size="18">save</v-icon>
                          Save
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-menu>
              </div>
              <v-divider class='my-2'></v-divider>
              <v-layout wrap>
                <v-flex xs12>
                  <v-select item-text="name" @change="update" label="Status" item-value="id" hide-details
                            v-model="task.column_id" :items="columns"></v-select>
                </v-flex>
              </v-layout>
              <v-layout wrap>
                <v-flex xs12 class="d-flex">
                  <v-select
                    multiple
                    @change="update"
                    label="Assignee(s)"
                    item-text="name"
                    deletable-chips
                    hide-details
                    class="pa-0"
                    chips
                    v-model="task.instance_users"
                    return-object
                    :items="instance_users"
                    style="flex: 1 1 90% !important;"
                  />
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        :disabled="task.instance_users && task.instance_users.length !== 1"
                        :loading="loading"
                        depressed
                        fab
                        dark
                        small
                        color="primary"
                        @click="addToWorkline"
                        style="max-width: 40px"
                        class="mr-0"
                      >
                        <v-icon size="18">add</v-icon>
                      </v-btn>
                    </template>
                    <span>Add to Workline.</span>
                  </v-tooltip>
                </v-flex>
              </v-layout>
              <v-layout wrap>
                <v-flex xs12>
                  <div class="task_page__list--labels">
                    <v-select multiple @change="update" item-text="name" label="Labels" deletable-chips hide-details
                              class="pa-0" chips
                              v-model="task.labels" return-object :items="labels"></v-select>
                  </div>
                </v-flex>
              </v-layout>

              <v-layout wrap>
                <v-flex xs12>
                  <div class="task_page__list--labels">
                    <v-text-field @change="update" label="External link" class="pa-0"
                                 v-model="task.external_link"></v-text-field>
                  </div>
                </v-flex>
              </v-layout>
              <v-layout wrap>
                <v-flex xs12>
                  <div class="task_page__list--labels">
                    <v-autocomplete
                        label="Parent task"
                        item-value="id"
                        hide-details
                        @change="attachParent"
                        v-model="task.parent_id"
                        :items="parent_tasks"
                    >
                      <template slot="item" slot-scope="data">
                        <div class="tasks_page__list--item_chip">{{ data.item.short_name.toUpperCase() }}</div>
                        <strong>{{ data.item.title }}</strong>
                      </template>
                      <template slot="selection" slot-scope="data">
                        <div class="tasks_page__list--item_chip">{{ data.item.short_name.toUpperCase() }}</div>
                        <strong>{{ data.item.title }}</strong>
                      </template>
                    </v-autocomplete>
                  </div>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>

          <v-card class="mt-3" xs4 v-if="task.attachments.length">
            <v-card-text>
              <v-layout>
                <v-flex xs12>
                  <div class='sl_form_title'>
                    <h3>Attached files</h3>
                  </div>
                  <v-divider class='my-2'></v-divider>
                  <div v-for="(file, index) in task.attachments" :key="index">
                    <v-layout wrap>
                      <v-flex>
                        <a :href="$baseURL + file.url" target="_blank">{{ file.name }}</a>
                      </v-flex>
                      <v-btn icon small @click="removeAttachment(index)">
                        <v-icon size="18">delete</v-icon>
                      </v-btn>
                    </v-layout>
                  </div>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>

        </v-flex>
      </v-layout>
      <v-layout wrap mt-3 v-if="task.id > 0 && hasFeature(project, $features.PROJECT_TIMES_BLOCK)">
        <v-flex>
          <v-card>
            <v-card-text>
              <v-layout>
                <v-flex xs12>
                  <div class='sl_form_title sl_form_title--extended'>
                    <h3>Event journal</h3>
                    <v-spacer></v-spacer>
                    <div v-if="!timer">
                      <v-btn small class="my-0" color="success" @click="startTimer">Start work</v-btn>
                    </div>
                    <div v-else>
                      <task-timer :time="timer"></task-timer>
                      <v-btn small class="my-0" color="info" @click="stopTimer">Stop the timer</v-btn>
                      <v-btn small class="my-0" color="error" @click="resetTimer">Reset the timer</v-btn>
                    </div>
                    <v-menu :close-on-content-click="false" close-on-click bottom offset-y min-width="280">
                      <v-btn slot="activator" class='ma-0' small :icon="isMobile" icon>
                        <v-icon>settings</v-icon>
                      </v-btn>
                      <v-card dense>
                        <v-card-text class="pa-2">
                          <h3 class="mb-1">Notifications</h3>
                          <v-divider></v-divider>
                          <v-checkbox
                            class="mt-0"
                            hide-details
                            color="success"
                            label="Time tracking"
                            v-model="taskWorkLog.state"
                          ></v-checkbox>
                          <div class="mt-3">
                            <v-btn block small elevation="2" color="success" @click="setLogNotifications">
                              <v-icon class="mr-1" size="18">save</v-icon>
                              Save
                            </v-btn>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-menu>
                  </div>
                  <v-divider class='my-2'></v-divider>
                  <task-times ref="times" :task_id="+$route.params.task_id" :notification="taskWorkLog"></task-times>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>

      </v-layout>

      <v-card v-if="!task.id">
        <v-card-text>
          <v-layout>
            <v-text-field v-model="parsing.url"/>
            <v-btn @click="parse">Parse</v-btn>
          </v-layout>
        </v-card-text>
      </v-card>


    </v-container>
    <div class="hidden_meta-data">
      <canvas id='canvas' height=40 width=40></canvas>
      <img id='image'>
    </div>
    <form-sub-task ref="FormSubTask" :parentTask="task" @refresh="refresh"></form-sub-task>
  </div>
</template>

<script>
  import * as mixins from '@/mixins'
  import times from '@/components/tasks/times'
  import comments from '@/components/tasks/comments/list'
  import children from '@/components/tasks/children'
  import markdown from '@/components/markdown'
  import drop from '@/components/drop'
  import timer from '@/components/timer'
  import formSubTask from '@/components/projects/form-sab-task'
  import {mapGetters} from "vuex";
  import taskTemplates from '@/entities/taskTemplates' // <-- import templates

  export default {
    name: 'TaskViewPage',
    mixins: [mixins.errors, mixins.history, mixins.preloader, mixins.removable],
    data() {
      return {
        api: this.$api.tasks,
        apiNotification: this.$api.notification,
        timer: false,
        isDeleted: false,
        imagePopupSrc: '',
        isEdit: false,
        parsing: {
          url: ''
        },
        columns: [],
        labels: [],
        instance_users: [],
        parent_tasks: [],
        task: {
          title: '',
          text: '',
          external_link: '',
          attachments: [],
          files: []
        },
        editing: {
          title: false,
          description: false,
          labels: [],
          instance_users: [],
          column_id: false,
        },
        project: {},
        ogImage: '',
        taskData: {
          type: 'TELEGRAM_TASK_STATUS_CHANGE',
          state: false
        },
        taskWorkLog: {
          type: 'TELEGRAM_WORKLOG_ENTRY_ADDED',
          state: false
        },
        dataManagementNotifications: [],
        dataLogNotifications: [],
        active: false,
        loading: false,
        taskTemplates, // <-- add templates to data
        selectedTemplate: null, // <-- selected template key
      }
    },
    computed: {
      ...mapGetters(['progress']),
      canSave() {
        return this.task.title.length <= 190
      }
    },
    methods: {
      getNotifications() {
        this.apiNotification.list(this.$route.params.project_id).then(response => {
          this.setTaskData(response.data.data)
        }, err => {
          console.log('pages/projects/tasks/form.vue getNotifications', err)
        })
      },
      editTask(data, focus = true) {
        if (!this.isEdit) this.edit(data, focus)
        this.isEdit = true
      },
      setTaskData(data) {
        data.forEach(fields => {
          if (fields.type === this.taskData.type) {
            this.taskData = fields
          } else if (fields.type === this.taskWorkLog.type) {
            this.taskWorkLog = fields
          }
        })
      },
      setDataManagementNotifications() {
        this.dataManagementNotifications = [
          this.taskData
        ]
      },
      setDataLogNotifications() {
        this.dataLogNotifications = [
          this.taskWorkLog
        ]
      },
      setLogNotifications() {
        this.setDataLogNotifications()
        this.apiNotification.store(this.$route.params.project_id, this.dataLogNotifications)
          .then(() => {
            this.$notify({
              group: 'success',
              type: 'success',
              text: 'Saved'
            })
          })
      },
      setManagementNotifications() {
        this.setDataManagementNotifications()
        this.apiNotification.store(this.$route.params.project_id, this.dataManagementNotifications)
            .then(() => {
              this.$notify({
                group: 'success',
                type: 'success',
                text: 'Saved'
              })
            })
      },
      paste(file) {
        this.task.attachments.push(file)
      },
      parse() {
        let code = this.parsing.url.replace("https://trello.com/c/", "")
        window.Trello.authorize({
          type: 'popup',
          name: 'Space',
          scope: {
            read: 'true',
            write: 'true'
          },
          expiration: 'never',
          success: () => {
            window.Trello.cards.get(code, (data) => {
              this.task.title = data.name
              this.task.text = this.parsing.url
            })
          }
        });
      },
      startTimer() {
        this.$api.timer.start().then(() => {
          this.queue(this.getTimer())
        })
      },
      stopTimer() {
        this.$api.timer.stop().then(({data}) => {
          this.$refs.times.setDuration(data.duration)
          this.queue(this.getTimer())
        })
      },
      resetTimer() {
        this.$api.timer.reset().then(() => {
          this.queue(this.getTimer())
        })
      },
      getTask() {
        return this.api.get(this.$route.params.task_id).then(({data}) => {
          this.task = data;
          this.getParentTasks();
          document.title = this.task.title;
        }).catch(() => {
          this.isDeleted = true
        })
      },
      getParentTasks() {
        let params = {
          unlimit: 1,
          projects: [
            this.$route.params.project_id
          ]
        }

        this.$api.tasks.list(params).then(({ data }) => {
          this.parent_tasks = data.filter(task => {
            let isCurrentTask = task.id === parseInt(this.$route.params.task_id)
            let isChildrenTask = this.isChildren(this.task, task.id) || this.task.children.some(child => {
              return this.isChildren(child, task.id)
            })

            return !isCurrentTask && !isChildrenTask
          })
        })
      },
      isChildren(parentTask, childId) {
        return parentTask.children && parentTask.children.some(child => {
          return child.id === childId || this.isChildren(child, childId)
        })
      },
      getProject() {
        return this.$api.projects.get(this.$route.params.project_id).then(({data}) => {
          let instance_users = data.instance_users.concat(data.instance_user)
          this.labels = data.labels
          this.instance_users = instance_users
          this.project = data
          this.columns = data.columns

        if (this.$route.params.task_id == 0) {
          this.task.column_id = data.columns[0].id
        }

        this.updateTitle("[SL][" + this.project.short_name + "] " + this.task.title)
      }, err => {
        console.log('pages/projects/tasks/form.vue getProject', err)
      })
    },
    getTimer() {
      return this.$api.timer.get().then(({data}) => {
        this.timer = data.timer
      })
    },
    saveTask() {
      this.editing.title = false
      this.editing.description = false

      return this.$api.tasks.save(this.task).then(({data}) => {
        this.task = {
          ...this.task,
          ...data,
          project: this.project
        }
        this.$notify({
          group: 'success',
          type: 'success',
          text: 'Saved'
        })
          this.$router.replace({name: 'tasks-form', params: {project_id: data.project_id, task_id: data.id}})
        }, err => {
        this.editing.title = true
        this.editing.description = true
        if (Object.values(err.errors).length) {
          this.errors = err.errors
          this.$notify({
            group: 'error',
            type: 'error',
            text: this.showFirstErrorMessage()
          })
        }
        console.log('pages/projects/tasks/form.vue saveTask', err)
      })
      },
      create() {
        this.background(this.saveTask())
      },
      attachParent(toAttachableId) {
        this.api.attach(toAttachableId, this.$route.params.task_id).then(() => {
          this.$notify({
            group: 'success',
            type: 'success',
            text: 'The task has been attached to parent task'
          })
        }, () => {
          this.$notify({
            group: 'error',
            type: 'error',
            text: 'Something went wrong.'
          })
        })
      },
      update() {
        if (this.task.id) {
          this.background(this.saveTask())
          this.isEdit = false
        }
      },
      removeTask(task) {
        this.remove(task, () => {
          this.$router.push({name: 'tasks-list', params: {project_id: this.task.project_id}})
        })
      },
      edit(field, focus = true) {
        this.editing[field] = !this.editing[field]
        if (this.editing[field] && focus) {
          setTimeout(() => {
            this.$refs[field].focus()
          }, 300)
        }
      },
      removeAttachment(index) {
        this.task.attachments.splice(index, 1)
        this.update()
      },
      forceDelete(task) {
        this.api.forceDelete(task.id).then(() => {
          this.$router.push({name: 'tasks-list', params: {project_id: this.task.project_id}})
        }, err => {
          console.log('pages/projects/tasks/form.vue forceDelete', err)
        })
      },
      restoreTask(task) {
        this.api.restore(task.id).then(() => {
          this.$router.push({name: 'tasks-list', params: {project_id: this.task.project_id}})
          this.$notify({
            group: 'success',
            type: 'success',
            text: ' The task has been restored from archive'
          })
        }, err => {
          console.log('pages/projects/tasks/form.vue restoreTask', err)
        })
      },
      shortNameToImg() {
        let imageElem = document.getElementById('image')
        let canvas = document.getElementById("canvas")
        let ctx = canvas.getContext("2d")
        let textXPosition = ctx.measureText(this.project.short_name).width

      ctx.fillStyle = "#14c570"
      ctx.fillRect(0, 0, 40, 40)
      ctx.fillStyle = "#fff"
      ctx.textAlign = 'center'
      ctx.fillText(this.project.short_name, textXPosition, 24)
      imageElem.src = ctx.canvas.toDataURL()
      this.ogImage = ctx.canvas.toDataURL()
    },
    setPageMetaData() {
      let metaList = [
        {prop: 'og:image', value: this.ogImage},
        {prop: 'og:title', value: this.task.title},
        {prop: 'og:url', value: location.href},
      ]

      metaList.forEach(item => {
        let meta = document.createElement('meta')
        meta.setAttribute('property', item.prop);
        meta.setAttribute('content', item.value);
        document.getElementsByTagName('head')[0].appendChild(meta)
      })
    },
    onMounted() {
      let task_id = parseInt(this.$route.params.task_id)

      if (task_id > 0) {
        this.queue(this.getTask())
      } else {
        this.edit('title')
        this.edit('description', false)

        this.task.project_id = this.$route.params.project_id
      }

        this.queue(this.getTimer())
        this.getProject().then(() => {
          this.shortNameToImg()
          this.setPageMetaData()
        })
      },
      refresh() {
        this.queue(this.onMounted())
      },
      addToWorkline() {
        const data = {
          user_id: this.task.instance_users[0].user_id,
          task_id: this.task.id,
        }
        this.loading = true
        this.$api.worklines.save(data).then(() => {
          this.$notify({
            group: 'success',
            type: 'success',
            text: 'The task has been added to workline'
          })
          this.loading = false
        }, error => {
          this.loading = false
          this.$notify({
            group: 'error',
            type: 'error',
            text: 'Something went wrong.'
          })
          console.error(error)
        })
      },
      onTemplateSelect(templateKey) {
        const template = this.taskTemplates.find(t => t.key === templateKey)
        if (!template) return
        if (this.task.text && this.task.text.trim().length > 0) {
          if (!window.confirm('Current task description will be replaced by the selected template. Continue?')) {
            this.selectedTemplate = null
            return
          }
        }
        this.task.text = template.value
      },
    },
    watch: {
      '$route.params.task_id'() {
        this.onMounted()
      },
      progress(value) {
        this.active = true
        Promise.all(value).then(() => {
          this.active = false
          this.$refs.comments.checkFocusedComment()
        });
      }
    },
    components: {
      'task-times': times,
      'task-comments': comments,
      'task-children': children,
      'markdown': markdown,
      'drop-zone': drop,
      'task-timer': timer,
      'form-sub-task': formSubTask,
    },
    mounted() {
      this.onMounted()
      this.getNotifications()
    }
  }
</script>

<style lang="scss" scoped>
.archive-wrapper {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}
</style>
